import { first } from 'lodash';

import { SlideEditorFormShape } from '@/modules/slideEditor/SlideEditor.types';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { getCarriedOverBundleFields } from '../../PresentationTemplateEditor.bundleFields';
import { PresentationTemplateEditorBundle } from '../../PresentationTemplateEditor.types';

export function mapPresentationTemplateBundleToSlideEditor(
  presentationTemplateBundle:
    | PresentationTemplateEditorBundle
    | null
    | undefined
): SlideEditorFormShape | null {
  if (!presentationTemplateBundle) {
    diagnostics.error('No presentation template bundle provided');
    return null;
  }
  const { kind, pages } = presentationTemplateBundle;

  // DividerBundle stores its data at the bundle level, not in pages.
  if (kind === PresentationBundleKind.DividerBundle) {
    return {
      kind,
      dividerPage: {
        dividerTitle:
          presentationTemplateBundle.dividerTemplateBundleConfiguration
            ?.dividerTitle ?? '',
        customContent:
          presentationTemplateBundle.dividerTemplateBundleConfiguration
            ?.customContent ?? '',
      },
    };
  }

  switch (kind) {
    case PresentationBundleKind.CoverSlideBundle: {
      const bundlePage = first(pages);
      if (!bundlePage) {
        diagnostics.error(`No page found in template bundle for ${kind}`);
        return null;
      }
      const { coverPage } = bundlePage;
      return {
        kind,
        coverPage: {
          bundle: null,
          page: {
            customContent: coverPage?.customContent || '',
            displayTitle: coverPage?.displayTitle || '',
            customPrompt: coverPage?.customPrompt ?? null,
          },
        },
      };
    }
    case PresentationBundleKind.CustomPageBundle: {
      const bundlePage = first(pages);
      if (!bundlePage) {
        diagnostics.error(`No page found in template bundle for ${kind}`);
        return null;
      }
      const { customPage } = bundlePage;
      return {
        kind,
        customPage: {
          bundle: {
            includePageHeader: customPage?.includePageHeader ?? false,
            pageHeader: customPage?.pageHeader || '',
            pageSubheader: customPage?.pageSubheader || '',
          },
          page: {
            pageName: customPage?.pageName || '',
            pageContent: customPage?.pageContent || '',
            customPrompt: customPage?.customPrompt ?? null,
          },
        },
      };
    }
    default:
      return {
        kind,
      };
  }
}

/**
 * Maps a SlideEditorFormShape payload back to a PresentationTemplateEditorBundle.
 * This is the inverse of mapPresentationTemplateBundleToSlideEditor.
 *
 * The bundle is recreated from the payload; the only things kept from the
 * original are the bundle/page IDs and the bundle-level fields the slide editor
 * doesn't own (see `getCarriedOverBundleFields`).
 *
 * If the return type is `null`, skip updating the template for this index.
 */
export function mapSlideEditorToPresentationTemplateBundle(
  payload: SlideEditorFormShape | null,
  originalBundle: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle | null {
  const originalPage = first(originalBundle.pages);

  if (!payload) {
    return null;
  }

  switch (payload.kind) {
    case PresentationBundleKind.CoverSlideBundle:
      return {
        ...getCarriedOverBundleFields(originalBundle),
        id: originalBundle.id,
        kind: originalBundle.kind,
        pages: [
          {
            id: originalPage?.id ?? '',
            kind: PresentationPageKind.CoverPage,
            coverPage: {
              id: originalPage?.coverPage?.id ?? '',
              customContent: payload.coverPage?.page.customContent ?? '',
              displayTitle: payload.coverPage?.page.displayTitle ?? '',
              customPrompt: payload.coverPage?.page.customPrompt ?? null,
            },
          },
        ],
      };
    case PresentationBundleKind.CustomPageBundle:
      return {
        ...getCarriedOverBundleFields(originalBundle),
        id: originalBundle.id,
        kind: originalBundle.kind,
        pages: [
          {
            id: originalPage?.id ?? '',
            kind: PresentationPageKind.CustomPage,
            customPage: {
              id: originalPage?.customPage?.id ?? '',
              pageName: payload.customPage?.page.pageName ?? '',
              pageContent: payload.customPage?.page.pageContent ?? '',
              includePageHeader:
                payload.customPage?.bundle.includePageHeader ?? false,
              pageHeader: payload.customPage?.bundle.pageHeader ?? '',
              pageSubheader: payload.customPage?.bundle.pageSubheader ?? '',
              customPrompt: payload.customPage?.page.customPrompt ?? null,
            },
          },
        ],
      };
    case PresentationBundleKind.DividerBundle:
      return {
        ...getCarriedOverBundleFields(originalBundle),
        id: originalBundle.id,
        kind: originalBundle.kind,
        dividerTemplateBundleConfiguration: {
          id: originalBundle.dividerTemplateBundleConfiguration?.id ?? '',
          dividerTitle: payload.dividerPage?.dividerTitle ?? '',
          customContent: payload.dividerPage?.customContent ?? '',
        },
        pages: [
          {
            id: originalPage?.id ?? '',
            kind: PresentationPageKind.Divider,
          },
        ],
      };
    default:
      return null;
  }
}
